import { RefreshCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const THRESHOLD = 70;
const MAX_PULL = 110;

/** True once `el` sits inside its own scrollable region (a modal body, a
 * dropdown list, …) rather than directly in the page — walked up from the
 * touch target so pulling down inside e.g. `Modal.tsx`'s `overflow-y-auto`
 * body scrolls that instead of triggering a page reload underneath it. */
function isInsideScrollable(el: EventTarget | null): boolean {
  let node = el instanceof Element ? el : null;
  while (node && node !== document.body) {
    const style = getComputedStyle(node);
    if ((style.overflowY === 'auto' || style.overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return true;
    }
    node = node.parentElement;
  }
  return false;
}

/** The common mobile-app gesture: pull down past the top of the page to
 * reload it — most native/PWA apps have it, this one didn't. A real
 * `window.location.reload()` (not a query refetch) on release past
 * `THRESHOLD`, which also happens to pick up a waiting service-worker
 * update (`registerType: 'autoUpdate'`, vite.config.ts) the same way a
 * manual browser refresh would.
 *
 * Handles the gesture itself rather than relying on the browser's native
 * pull-to-refresh: iOS Safari (incl. "Zum Home-Bildschirm") has none at
 * all, and Android Chrome's own version is unreliable once installed as a
 * standalone PWA — `overscroll-behavior-y: contain` on `body` (index.css)
 * suppresses it everywhere so this is the only one that ever fires. */
export function PullToRefresh() {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef<number | null>(null);
  const tracking = useRef(false);

  useEffect(() => {
    function onTouchStart(e: TouchEvent) {
      if (refreshing || window.scrollY > 0 || isInsideScrollable(e.target)) return;
      startY.current = e.touches[0]!.clientY;
      tracking.current = true;
    }

    function onTouchMove(e: TouchEvent) {
      if (!tracking.current || startY.current === null) return;
      if (window.scrollY > 0) {
        tracking.current = false;
        setPull(0);
        return;
      }
      const delta = e.touches[0]!.clientY - startY.current;
      if (delta <= 0) {
        setPull(0);
        return;
      }
      // Own the gesture once it's clearly a downward pull at the top —
      // keeps the browser's own rubber-band bounce from fighting our
      // indicator underneath it.
      if (e.cancelable) e.preventDefault();
      setPull(Math.min(delta, MAX_PULL));
    }

    function onTouchEnd() {
      if (!tracking.current) return;
      tracking.current = false;
      startY.current = null;
      setPull((current) => {
        if (current >= THRESHOLD) {
          setRefreshing(true);
          window.location.reload();
          return current;
        }
        return 0;
      });
    }

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('touchcancel', onTouchEnd);
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [refreshing]);

  if (pull === 0 && !refreshing) return null;

  const ready = pull >= THRESHOLD;
  // A normal block, not `position: fixed` — its height pushes the header/
  // content down as the pull grows, the same "makes room as you pull" look
  // native apps have, instead of a floating badge overlapping the page.
  const height = refreshing ? 48 : Math.min(pull, MAX_PULL);

  return (
    <div aria-hidden="true" className="flex items-center justify-center overflow-hidden" style={{ height }}>
      <RefreshCw
        size={20}
        className={refreshing ? 'animate-spin text-brand-accent' : ready ? 'text-brand-accent' : 'text-slate-400'}
        style={refreshing ? undefined : { transform: `rotate(${(pull / THRESHOLD) * 180}deg)` }}
      />
    </div>
  );
}
